<template>
  <div ref="root" class="castle">
    <canvas ref="canvas" class="canvas"></canvas>
  </div>
</template>

<script setup lang="ts">
/* eslint-disable no-mixed-operators */
import { Box3, Vector3 } from 'three'
import { onBeforeUnmount, onMounted, ref, PropType } from 'vue'
import { RafCallback, useResize } from '@/utils/aware'
import { gsap } from 'gsap'
import ScrollTrigger from 'gsap/ScrollTrigger'

import { World, WorldConfig } from './World'
import { useUiStore } from '@/stores/ui'
import Ticker from '@/modules/Ticker'

interface CastleConfig extends WorldConfig {
  scrollIn: boolean
  isConfigurable: boolean
}

gsap.registerPlugin(ScrollTrigger)

const props = defineProps({
  content: {
    type: Object,
    default: () => ({}),
    required: false,
  },
  config: {
    type: Object as PropType<CastleConfig>,
    default: () => ({
      scrollIn: false,
      isConfigurable: false,
      reversed: false,
      rotation: true,
      background: false,
      panels: false,
    }),
  },
})

const ui = useUiStore()

let ticker: Ticker
const isVisible = ref(false)
const canvas = ref<HTMLCanvasElement>({} as HTMLCanvasElement)
const root = ref<HTMLElement>({} as HTMLElement)
const castleData = {
  bufferX: 2.5,
  world: {} as World,
  width: 0,
  height: 0,
  size: new Vector3(0),
  viewport: { width: 0, height: 0 },
}

const draw: RafCallback = () => {
  if (!castleData.world || !isVisible.value) {
    return
  }

  castleData.world.render?.()
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const onWindowResize = (...args: any[]) => {
  const [canvas] = args
  if (canvas) {
    castleData.width = canvas.offsetWidth
    castleData.height = canvas.offsetHeight
    castleData.world &&
      castleData.world.resize(castleData.width, castleData.height)
  }
}

const setup = (canvas: HTMLCanvasElement) => {
  castleData.world = new World({ canvas, config: props.config as WorldConfig })

  return castleData.world.loading
}

const setInitialPosition = () => {
  const boundingBox = new Box3().setFromObject(castleData.world.castle.holder)
  castleData.size = boundingBox.getSize(new Vector3())
  castleData.viewport = castleData.world.calculateUnitSize()
  castleData.world.castle.holder.position.setX(
    // Extracting this data into variable makes it to break
    props.config.reversed
      ? (castleData.viewport.width + castleData.size.x + castleData.bufferX) / 2
      : -(castleData.viewport.width + castleData.size.x + castleData.bufferX) /
      2
  )
}

const initScrollTrigger = () => {
  let animation

  if (props.config.scrollIn) {
    setInitialPosition()
    animation = initAnimation()
  }

  ScrollTrigger.create({
    animation: animation ? animation.position : undefined,
    start: 'top bottom',
    end: 'bottom top',
    onUpdate: ({ direction }) => {
      castleData.world.config.reversedRotation = direction < 0
    },
    onEnter: () => {
      isVisible.value = true
    },
    onEnterBack: () => {
      castleData.world.config.reversedRotation =
        !castleData.world.config.reversedRotation
      isVisible.value = true
    },
    onLeave: () => {
      isVisible.value = false
    },
    onLeaveBack: () => {
      isVisible.value = false
    },
    trigger: canvas.value,
    scrub: 1,
  })
}

const initAnimation = () => {
  const x = castleData.viewport.width

  return {
    position: gsap.to(castleData.world.castle.holder.position, {
      x: props.config.reversed ? -x : x,
      ease: 'none',
    }),
  }
}

onMounted(() => {
  setup(canvas.value).then(() => {
    ticker = new Ticker(draw)

    if (ui.deviceTier < 2) {
      ticker.fps(30)
    }

    useResize(onWindowResize.bind(null, root.value))
    initScrollTrigger()
  })
})

onBeforeUnmount(() => {
  ticker.fps(60)
  ticker.destroy()
  isVisible.value = false
})
</script>

<style lang="scss" scoped>
.castle {
  position: relative;
  padding-top: math.div(9, 16) * 100%;
}

.canvas {
  @include get-all-space;

  width: 100%;
}
</style>
